import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import pendingSvg from '@assets/pending.svg';
import {
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { colors } from '../../../styles/theme';
import CustomButton from '../Button/Custombutton';
import { useTheme } from '../../../../core/providers/ThemeProvider';
import { useResponsive, getResponsiveValue } from '../../../utils/responsive';

const AlertIcon: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <Box sx={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <svg width={size} height={size} viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g opacity="0.3">
        <path
          d="M19 6C26.1797 6 32 11.8203 32 19C32 26.1797 26.1797 32 19 32C11.8203 32 6 26.1797 6 19C6 11.8203 11.8203 6 19 6Z"
          stroke={color}
          strokeWidth="2"
        />
      </g>
      <g opacity="0.1">
        <path
          d="M19 1C28.9411 1 37 9.05887 37 19C37 28.9411 28.9411 37 19 37C9.05887 37 1 28.9411 1 19C1 9.05887 9.05887 1 19 1Z"
          stroke={color}
          strokeWidth="2"
        />
      </g>
      <g clipPath="url(#clip0_0_1)">
        <path
          d="M19 22.3337V19.0003M19 15.667H19.0083M27.3333 19.0003C27.3333 23.6027 23.6023 27.3337 19 27.3337C14.3976 27.3337 10.6666 23.6027 10.6666 19.0003C10.6666 14.398 14.3976 10.667 19 10.667C23.6023 10.667 27.3333 14.398 27.3333 19.0003Z"
          stroke={color}
          strokeWidth="1.66667"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_0_1">
          <rect width="20" height="20" fill="white" transform="translate(9 9)"/>
        </clipPath>
      </defs>
    </svg>
  </Box>
);

const QuestionIcon: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <Box sx={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <svg width={size} height={size} viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g opacity="0.3">
        <path
          d="M19 6C26.1797 6 32 11.8203 32 19C32 26.1797 26.1797 32 19 32C11.8203 32 6 26.1797 6 19C6 11.8203 11.8203 6 19 6Z"
          stroke={color}
          strokeWidth="2"
        />
      </g>
      <g opacity="0.1">
        <path
          d="M19 1C28.9411 1 37 9.05887 37 19C37 28.9411 28.9411 37 19 37C9.05887 37 1 28.9411 1 19C1 9.05887 9.05887 1 19 1Z"
          stroke={color}
          strokeWidth="2"
        />
      </g>
      <g clipPath="url(#clip0_question)">
        <path
          d="M17.0667 17C17.0667 16.0667 17.8667 15.3333 18.8 15.3333C19.7333 15.3333 20.5333 16.0667 20.5333 17C20.5333 17.6 20.2 18.1333 19.7333 18.4C19.3333 18.6 19 19 19 19.4667V20M19 22.6667H19.0083M27.3333 19C27.3333 23.6027 23.6023 27.3337 19 27.3337C14.3976 27.3337 10.6666 23.6027 10.6666 19C10.6666 14.398 14.3976 10.667 19 10.667C23.6023 10.667 27.3333 14.398 27.3333 19Z"
          stroke={color}
          strokeWidth="1.66667"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_question">
          <rect width="20" height="20" fill="white" transform="translate(9 9)"/>
        </clipPath>
      </defs>
    </svg>
  </Box>
);

const TimerIcon: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <Box sx={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <svg width={size} height={size} viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g opacity="0.3">
        <path
          d="M19 6C26.1797 6 32 11.8203 32 19C32 26.1797 26.1797 32 19 32C11.8203 32 6 26.1797 6 19C6 11.8203 11.8203 6 19 6Z"
          stroke={color}
          strokeWidth="2"
        />
      </g>
      <g opacity="0.1">
        <path
          d="M19 1C28.9411 1 37 9.05887 37 19C37 28.9411 28.9411 37 19 37C9.05887 37 1 28.9411 1 19C1 9.05887 9.05887 1 19 1Z"
          stroke={color}
          strokeWidth="2"
        />
      </g>
      <g clipPath="url(#clip0_timer)">
        <path
          d="M15.6667 11.3333H22.3333M19 14V19L22.3333 22.3333M27.3333 19C27.3333 23.6027 23.6023 27.3337 19 27.3337C14.3976 27.3337 10.6666 23.6027 10.6666 19C10.6666 14.398 14.3976 10.667 19 10.667C23.6023 10.667 27.3333 14.398 27.3333 19Z"
          stroke={color}
          strokeWidth="1.66667"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_timer">
          <rect width="20" height="20" fill="white" transform="translate(9 9)"/>
        </clipPath>
      </defs>
    </svg>
  </Box>
);

const ExclamationIcon: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <Box sx={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <svg width={size} height={size} viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g opacity="0.3">
        <path
          d="M19 6C26.1797 6 32 11.8203 32 19C32 26.1797 26.1797 32 19 32C11.8203 32 6 26.1797 6 19C6 11.8203 11.8203 6 19 6Z"
          stroke={color}
          strokeWidth="2"
        />
      </g>
      <g opacity="0.1">
        <path
          d="M19 1C28.9411 1 37 9.05887 37 19C37 28.9411 28.9411 37 19 37C9.05887 37 1 28.9411 1 19C1 9.05887 9.05887 1 19 1Z"
          stroke={color}
          strokeWidth="2"
        />
      </g>
      <g clipPath="url(#clip0_exclamation)">
        <path
          d="M19 15.667V19.0003M19 22.3337H19.0083M27.3333 19.0003C27.3333 23.6027 23.6023 27.3337 19 27.3337C14.3976 27.3337 10.6666 23.6027 10.6666 19.0003C10.6666 14.398 14.3976 10.667 19 10.667C23.6023 10.667 27.3333 14.398 27.3333 19.0003Z"
          stroke={color}
          strokeWidth="1.66667"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_exclamation">
          <rect width="20" height="20" fill="white" transform="translate(9 9)"/>
        </clipPath>
      </defs>
    </svg>
  </Box>
);

const SuccessIcon: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <Box sx={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <svg width={size} height={size} viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute' }}>
      <g opacity="0.3">
        <path
          d="M19 6C26.1797 6 32 11.8203 32 19C32 26.1797 26.1797 32 19 32C11.8203 32 6 26.1797 6 19C6 11.8203 11.8203 6 19 6Z"
          stroke={color}
          strokeWidth="2"
        />
      </g>
      <g opacity="0.1">
        <path
          d="M19 1C28.9411 1 37 9.05887 37 19C37 28.9411 28.9411 37 19 37C9.05887 37 1 28.9411 1 19C1 9.05887 9.05887 1 19 1Z"
          stroke={color}
          strokeWidth="2"
        />
      </g>
    </svg>
    <CheckCircleIcon
      sx={{
        width: size * 0.57,
        height: size * 0.57,
        color: color,
      }}
    />
  </Box>
);

const statusIconMap = {
  info: InfoIcon,
  success: CheckCircleIcon,
  warning: WarningIcon,
  alert: WarningIcon,
  error: ErrorIcon,
  neutral: InfoIcon,
};

export interface StatusMessageProps {
  status?: 'info' | 'success' | 'error' | 'warning' | 'alert' | 'neutral' | 'kycVerified' | 'kycPending' | 'kycCanceled' | 'kycRestricted' | 'kycRequiresInput' | 'kycProcessing' | 'kycCardPending' | 'payoutPending' | 'payoutProcessing' | 'payoutRefunded' | 'payoutInitiated';
  message: string;
  title?: string;
  messageNode?: React.ReactNode;
  showIcon?: boolean;
  iconType?: 'info' | 'success' | 'warning' | 'alert' | 'error' | 'question' | 'timer' | 'exclamation' | 'check';
  customIcon?: React.ReactNode;
  showButton?: boolean;
  buttonText?: string;
  buttonDisabled?: boolean;
  onButtonClick?: () => void;
  showCloseButton?: boolean;
  onClose?: () => void;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  iconColor?: string;
  buttonColor?: string;
  width?: number | string;
  height?: number | string;
  buttonHeight?: number | string;
  buttonWidth?: number | string;
  buttonFontSize?: number | string;
  buttonPadding?: string;
  alignButtonWithText?: boolean;
  inlineButton?: boolean;
  fontSize?: number | string;
  forceStacked?: boolean;
  className?: string;
  layout?: 'default' | 'banner';
  size?: 'default' | 'compact';
}

const StatusMessage: React.FC<StatusMessageProps> = ({
  status = 'info',
  message,
  title,
  messageNode,
  showIcon = true,
  iconType,
  customIcon,
  showButton = true,
  buttonText = 'Create Account',
  buttonDisabled = false,
  onButtonClick,
  showCloseButton = true,
  onClose,
  backgroundColor,
  borderColor,
  textColor,
  iconColor,
  buttonColor,
  width,
  height,
  buttonHeight: buttonHeightOverride,
  buttonWidth,
  buttonFontSize,
  buttonPadding,
  alignButtonWithText = false,
  inlineButton = false,
  fontSize: fontSizeOverride,
  forceStacked = false,
  className = '',
  layout = 'default',
  size = 'default',
}) => {
  const { theme } = useTheme();
  const { isMobile, isTablet, breakpoint } = useResponsive();
  
  const statusColorSchemes = {
    info: {
      background: '#FDF2F8',
      border: '#F9A8D4',
      iconColor: '#7A0056',
      textColor: '#831843',
      buttonColor: '#7A0056',
    },
    success: {
      background: '#DCFCE7',
      border: '#86EFAC',
      iconColor: '#1CA34E',
      textColor: '#15803D',
      buttonColor: '#1CA34E',
    },
    error: {
      background: '#FEE2E2',
      border: '#FECACA',
      iconColor: '#D12A2A',
      textColor: '#991B1B',
      buttonColor: '#D12A2A',
    },
    warning: {
      background: '#FEF3C7',
      border: '#FDE68A',
      iconColor: '#A57B00',
      textColor: '#92400E',
      buttonColor: '#A57B00',
    },
    alert: {
      background: '#F5E8EF',
      border: '#9B1B5A',
      iconColor: '#7A0056',
      textColor: '#831843',
      buttonColor: '#7A0056',
    },
    neutral: {
      background: '#F3F4F6',
      border: '#D1D5DB',
      iconColor: '#777B8B',
      textColor: '#4B5563',
      buttonColor: '#777B8B',
    },
    kycVerified: {
      background: '#E9F7EE',
      border: '#1FB356',
      iconColor: '#1FB356',
      textColor: '#5D606D',
      buttonColor: '#1FB356',
    },
    kycPending: {
      background: '#FEF3C7',
      border: '#FDE68A',
      iconColor: '#A57B00',
      textColor: '#92400E',
      buttonColor: '#A57B00',
    },
    kycCanceled: {
      background: '#FDEAEA',
      border: '#E62E2E',
      iconColor: '#E62E2E',
      textColor: '#5D606D',
      buttonColor: '#D12A2A',
    },
    kycRestricted: {
      background: '#FDEAEA',
      border: '#E62E2E',
      iconColor: '#E62E2E',
      textColor: '#5D606D',
      buttonColor: '#D12A2A',
    },
    kycRequiresInput: {
      background: '#F5E8EF',
      border: '#9B1B5A',
      iconColor: '#9B1B5A',
      textColor: '#5D606D',
      buttonColor: '#9B1B5A',
    },
    kycProcessing: {
      background: '#F4F3FF',
      border: '#5925DC',
      iconColor: '#5925DC',
      textColor: '#5D606D',
      buttonColor: '#5925DC',
    },
    kycCardPending: {
      background: colors.kycBanner.background,
      border: colors.kycBanner.border,
      iconColor: colors.kycBanner.icon,
      textColor: colors.kycBanner.text,
      buttonColor: colors.kycBanner.button,
    },
    payoutPending: {
      background: '#FEF6EE',
      border: '#F9DBAF',
      iconColor: '#DC6803',
      textColor: '#5D606D',
      buttonColor: '#DC6803',
    },
    payoutProcessing: {
      background: '#FDF7E6',
      border: '#E8AD00',
      iconColor: '#DC6803',
      textColor: '#5D606D',
      buttonColor: '#A57B00',
    },
    payoutRefunded: {
      background: '#EEF4FF',
      border: '#C7D7FE',
      iconColor: '#3538CD',
      textColor: '#5D606D',
      buttonColor: '#3538CD',
    },
    payoutInitiated: {
      background: '#F5E8EF',
      border: '#7A0056',
      iconColor: '#7A0056',
      textColor: '#5D606D',
      buttonColor: '#7A0056',
    },
  };
  
  const colorScheme = statusColorSchemes[status];
  const finalBackgroundColor = backgroundColor || colorScheme.background;
  const finalBorderColor = borderColor || colorScheme.border;
  const finalTextColor = textColor || colorScheme.textColor;
  const finalIconColor = iconColor || colorScheme.iconColor;
  const finalButtonColor = buttonColor || colorScheme.buttonColor;
  
  const StatusIcon = statusIconMap[iconType || status];
  const shouldUseCustomAlertIcon = !customIcon && (iconType === 'alert' || status === 'alert' || iconType === 'info' || status === 'info' || status === 'kycPending' || status === 'kycCardPending');
  const shouldUseSuccessIcon = !customIcon && (iconType === 'success' || status === 'success' || iconType === 'check' || status === 'kycVerified');
  const shouldUseQuestionIcon = !customIcon && (iconType === 'question' || status === 'kycRequiresInput');
  const shouldUseTimerIcon = !customIcon && (iconType === 'timer' || status === 'kycProcessing');
  const shouldUseExclamationIcon = !customIcon && (iconType === 'exclamation' || status === 'kycCanceled' || status === 'kycRestricted');
  
  const isCompact = size === 'compact';

  const responsiveWidth = width || '100%';
  const responsiveHeight = height || (isCompact ? 'auto' : getResponsiveValue({
    mobile: 'auto',
    tablet: 'auto',
    desktop: '66px',
  }, breakpoint));

  const paddingValues = isCompact
    ? getResponsiveValue({
        mobile: '6px 6px 6px 8px',
        tablet: '8px 8px 8px 10px',
        desktop: '8px 10px 8px 12px',
      }, breakpoint)
    : getResponsiveValue({
        mobile: '10px 10px 10px 14px',
        tablet: '14px 14px 14px 16px',
        desktop: '14px 16px 14px 20px',
      }, breakpoint);

  const iconSize = isCompact
    ? getResponsiveValue({
        mobile: 16,
        tablet: 18,
        desktop: 20,
      }, breakpoint)
    : getResponsiveValue({
        mobile: 26,
        tablet: 32,
        desktop: 37,
      }, breakpoint);

  const elementGap = getResponsiveValue({
    mobile: '8px',
    tablet: '10px',
    desktop: isCompact ? '12px' : '16px',
  }, breakpoint);

  const fontSize = fontSizeOverride ?? (isCompact
    ? getResponsiveValue({
        mobile: '12px',
        tablet: '12px',
        desktop: '13px',
      }, breakpoint)
    : getResponsiveValue({
        mobile: '14px',
        tablet: '14px',
        desktop: '15px',
      }, breakpoint));

  const buttonHeight = buttonHeightOverride ?? getResponsiveValue({
    mobile: '32px',
    tablet: '34px',
    desktop: '34px',
  }, breakpoint);
  
  const isStackedLayout = forceStacked || (isMobile && message.length > 40);

  const bannerColorSchemes: Record<string, { background: string; border: string; icon: string; button: string; buttonHover: string }> = {
    kycPending: {
      background: colors.payoutBanner.warning.background,
      border:     colors.payoutBanner.warning.border,
      icon:       colors.payoutBanner.warning.icon,
      button:     colors.payoutBanner.warning.button,
      buttonHover: colors.payoutBanner.warning.buttonHover,
    },
    kycVerified: {
      background: colors.payoutBanner.success.background,
      border:     colors.payoutBanner.success.border,
      icon:       colors.payoutBanner.success.icon,
      button:     colors.payoutBanner.success.button,
      buttonHover: colors.payoutBanner.success.buttonHover,
    },
    kycCanceled: {
      background: colors.payoutBanner.error.background,
      border:     colors.payoutBanner.error.border,
      icon:       colors.payoutBanner.error.icon,
      button:     colors.payoutBanner.error.button,
      buttonHover: colors.payoutBanner.error.buttonHover,
    },
    kycRestricted: {
      background: colors.payoutBanner.error.background,
      border:     colors.payoutBanner.error.border,
      icon:       colors.payoutBanner.error.icon,
      button:     colors.payoutBanner.error.button,
      buttonHover: colors.payoutBanner.error.buttonHover,
    },
    kycProcessing: {
      background: colors.payoutBanner.processing.background,
      border:     colors.payoutBanner.processing.border,
      icon:       colors.payoutBanner.processing.icon,
      button:     colors.payoutBanner.processing.button,
      buttonHover: colors.payoutBanner.processing.buttonHover,
    },
    kycRequiresInput: {
      background: colors.payoutBanner.warning.background,
      border:     colors.payoutBanner.warning.border,
      icon:       colors.payoutBanner.warning.icon,
      button:     colors.payoutBanner.warning.button,
      buttonHover: colors.payoutBanner.warning.buttonHover,
    },
    payoutPending: {
      background: colors.payoutBanner.warning.background,
      border:     colors.payoutBanner.warning.border,
      icon:       colors.payoutBanner.warning.icon,
      button:     colors.payoutBanner.warning.button,
      buttonHover: colors.payoutBanner.warning.buttonHover,
    },
    payoutRefunded: {
      background: colors.payoutBanner.blue.background,
      border:     colors.payoutBanner.blue.border,
      icon:       colors.payoutBanner.blue.icon,
      button:     colors.payoutBanner.blue.button,
      buttonHover: colors.payoutBanner.blue.buttonHover,
    },
    payoutProcessing: {
      background: colors.payoutBanner.warning.background,
      border:     colors.payoutBanner.warning.border,
      icon:       colors.payoutBanner.warning.icon,
      button:     colors.payoutBanner.warning.button,
      buttonHover: colors.payoutBanner.warning.buttonHover,
    },
    payoutInitiated: {
      background: colors.payoutBanner.pink.background,
      border:     colors.payoutBanner.pink.border,
      icon:       colors.payoutBanner.pink.icon,
      button:     colors.payoutBanner.pink.button,
      buttonHover: colors.payoutBanner.pink.buttonHover,
    },
  };

  if (layout === 'banner') {
    const bc = bannerColorSchemes[status] ?? bannerColorSchemes.kycPending;
    const bannerIconColor = iconColor || bc.icon;
    const bannerButtonColor = buttonColor || bc.button;
    const ICON_SIZE = 28;
    const bannerShouldUseAlert       = !customIcon && (status === 'kycPending' || status === 'kycRequiresInput' || status === 'payoutPending' || status === 'payoutProcessing');
    const bannerShouldUseSuccess     = !customIcon && status === 'kycVerified';
    const bannerShouldUseTimer       = !customIcon && (status === 'kycProcessing' || status === 'payoutRefunded');
    const bannerShouldUseExclamation = !customIcon && (status === 'kycCanceled' || status === 'kycRestricted');

    const PendingIcon = ({ size, color }: { size: number; color: string }) => (
      <Box sx={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ position: 'absolute', inset: `-${size * 0.375}px`, borderRadius: '50%', border: `1.67px solid ${color}`, opacity: 0.1 }} />
        <Box sx={{ position: 'absolute', inset: `-${size * 0.167}px`, borderRadius: '50%', border: `1.67px solid ${color}`, opacity: 0.3 }} />
        <Box sx={{ width: size, height: size, borderRadius: '50%', border: `1.67px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box component="img" src={pendingSvg} alt="" sx={{ width: size * 0.55, height: size * 0.55 }} />
        </Box>
      </Box>
    );

    const BannerIcon = () => {
      if (customIcon) return <>{customIcon}</>;
      if (bannerShouldUseSuccess)     return <SuccessIcon     size={ICON_SIZE} color={bannerIconColor} />;
      if (bannerShouldUseTimer)       return <PendingIcon     size={ICON_SIZE} color={bannerIconColor} />;
      if (bannerShouldUseExclamation) return <ExclamationIcon size={ICON_SIZE} color={bannerIconColor} />;
      if (bannerShouldUseAlert)       return <AlertIcon       size={ICON_SIZE} color={bannerIconColor} />;
      return <AlertIcon size={ICON_SIZE} color={bannerIconColor} />;
    };

    return (
      <Box
        className={className}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '16px',
          gap: '12px',
          backgroundColor: backgroundColor || bc.background,
          border: `1px solid ${borderColor || bc.border}`,
          borderRadius: '12px',
          boxShadow: '0px 1px 2px rgba(10, 13, 18, 0.05)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '16px',
            width: '100%',
          }}
        >
          {showIcon && (
            <Box sx={{ flexShrink: 0, width: ICON_SIZE, height: ICON_SIZE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BannerIcon />
            </Box>
          )}

          <Typography
            sx={{
              flex: 1,
              fontFamily: 'Poppins',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '21px',
              color: colors.payoutBanner.titleColor,
            }}
          >
            {title || message}
          </Typography>

          {showCloseButton && (
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                p: 0,
                width: '20px',
                height: '20px',
                flexShrink: 0,
                color: colors.payoutBanner.closeColor,
                '&:hover': { backgroundColor: 'transparent', opacity: 0.7 },
              }}
              aria-label="Close"
            >
              <CloseIcon sx={{ width: '16px', height: '16px' }} />
            </IconButton>
          )}
        </Box>

        {title && (
          messageNode ? (
            <>{messageNode}</>
          ) : (
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '21px',
                color: colors.payoutBanner.bodyColor,
              }}
            >
              {message}
            </Typography>
          )
        )}

        {showButton && buttonText && (
          <CustomButton
            buttonText={buttonText}
            onClick={onButtonClick}
            disabled={buttonDisabled}
            size="xs"
            sx={{
              height: '34px',
              backgroundColor: bannerButtonColor,
              color: '#FFFFFF',
              fontFamily: 'Poppins',
              fontWeight: 500,
              fontSize: '12px',
              lineHeight: '18px',
              borderRadius: '8px',
              padding: '8px 12px',
              textTransform: 'none',
              boxShadow: 'none',
              whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: buttonColor ? `${buttonColor}CC` : bc.buttonHover, boxShadow: 'none' },
              '&.MuiButton-root': { boxShadow: 'none' },
            }}
          />
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: typeof responsiveWidth === 'number' ? `${responsiveWidth}px` : responsiveWidth,
        height: typeof responsiveHeight === 'number' ? `${responsiveHeight}px` : responsiveHeight,
        display: 'flex',
        flexDirection: isStackedLayout ? 'column' : 'row',
        alignItems: isStackedLayout ? 'flex-start' : 'center',
        gap: elementGap,
        backgroundColor: finalBackgroundColor,
        border: `1px solid ${finalBorderColor}`,
        borderRadius: '12px', 
        padding: paddingValues,
        position: 'relative',
      }}
      className={className}
    >
      {isStackedLayout ? (
        <>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '8px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flex: 1 }}>
              {showIcon && (
                <Box
                  sx={{
                    width: iconSize,
                    height: iconSize,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '2px',
                  }}
                >
                  {customIcon || (
                    shouldUseCustomAlertIcon ? (
                      <AlertIcon size={iconSize} color={finalIconColor} />
                    ) : shouldUseSuccessIcon ? (
                      <SuccessIcon size={iconSize} color={finalIconColor} />
                    ) : shouldUseQuestionIcon ? (
                      <QuestionIcon size={iconSize} color={finalIconColor} />
                    ) : shouldUseTimerIcon ? (
                      <TimerIcon size={iconSize} color={finalIconColor} />
                    ) : shouldUseExclamationIcon ? (
                      <ExclamationIcon size={iconSize} color={finalIconColor} />
                    ) : (
                      <StatusIcon sx={{ width: iconSize, height: iconSize, color: finalIconColor }} />
                    )
                  )}
                </Box>
              )}

              <Box
                sx={{
                  fontSize: fontSize,
                  fontWeight: 500,
                  color: finalTextColor,
                  fontFamily: theme.typography.bodyText.fontFamily,
                  lineHeight: '20px',
                }}
              >
                {messageNode || message}
                {inlineButton && showButton && (
                  <CustomButton
                    buttonText={buttonText}
                    onClick={onButtonClick}
                    disabled={buttonDisabled}
                    size="xs"
                    sx={{
                      display: 'inline-flex',
                      verticalAlign: 'middle',
                      marginLeft: '8px',
                      marginTop: '4px',
                      width: buttonWidth ?? 'auto',
                      height: buttonHeight,
                      backgroundColor: finalButtonColor,
                      color: colors.neutral.white,
                      fontSize: buttonFontSize ?? '13px',
                      fontWeight: 600,
                      fontFamily: theme.typography.bodyText.fontFamily,
                      borderRadius: '8px',
                      padding: buttonPadding ?? '8px 16px',
                      textTransform: 'none',
                      boxShadow: 'none',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        backgroundColor: finalButtonColor,
                        filter: 'brightness(1.1)',
                        boxShadow: 'none',
                      },
                      '&.MuiButton-root': { boxShadow: 'none' },
                    }}
                  />
                )}
              </Box>
            </Box>

            {showCloseButton && (
              <IconButton
                onClick={onClose}
                sx={{
                  width: '32px',
                  height: '32px',
                  padding: '6px',
                  borderRadius: '6px',
                  color: finalIconColor,
                  flexShrink: 0,
                  marginTop: '-4px',
                  marginRight: '-4px',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
                aria-label="Close"
              >
                <CloseIcon sx={{ width: '18px', height: '18px' }} />
              </IconButton>
            )}
          </Box>

          {showButton && !inlineButton && (
            <Box sx={{
              width: buttonWidth ? 'auto' : '100%',
              marginTop: '6px',
              marginLeft: alignButtonWithText && showIcon ? `calc(${typeof iconSize === 'number' ? `${iconSize}px` : iconSize} + 8px)` : 0,
            }}>
              <CustomButton
                buttonText={buttonText}
                onClick={onButtonClick}
                disabled={buttonDisabled}
                size="xs"
                sx={{
                  width: buttonWidth ?? '100%',
                  height: buttonHeight,
                  backgroundColor: finalButtonColor,
                  color: colors.neutral.white,
                  fontSize: buttonFontSize ?? '13px',
                  fontWeight: 600,
                  fontFamily: theme.typography.bodyText.fontFamily,
                  borderRadius: '8px',
                  padding: buttonPadding ?? '8px 16px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: finalButtonColor,
                    filter: 'brightness(1.1)',
                    boxShadow: 'none',
                  },
                  '&.MuiButton-root': { boxShadow: 'none' },
                }}
              />
            </Box>
          )}
        </>
      ) : (
        <>
          {showIcon && (
            <Box
              sx={{
                width: iconSize,
                height: iconSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {customIcon || (
                shouldUseCustomAlertIcon ? (
                  <AlertIcon size={iconSize} color={finalIconColor} />
                ) : shouldUseSuccessIcon ? (
                  <SuccessIcon size={iconSize} color={finalIconColor} />
                ) : shouldUseQuestionIcon ? (
                  <QuestionIcon size={iconSize} color={finalIconColor} />
                ) : shouldUseTimerIcon ? (
                  <TimerIcon size={iconSize} color={finalIconColor} />
                ) : shouldUseExclamationIcon ? (
                  <ExclamationIcon size={iconSize} color={finalIconColor} />
                ) : (
                  <StatusIcon sx={{ width: iconSize, height: iconSize, color: finalIconColor }} />
                )
              )}
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              fontSize: fontSize,
              fontWeight: 500,
              color: finalTextColor,
              fontFamily: theme.typography.bodyText.fontFamily,
              lineHeight: isCompact ? '17px' : '20px',
            }}
          >
            {messageNode || message}
          </Box>

          {showButton && (
            <CustomButton
              buttonText={buttonText}
              onClick={onButtonClick}
              disabled={buttonDisabled}
              size="xs"
              sx={{
                width: 'auto',
                minWidth: isMobile ? '80px' : '100px',
                height: buttonHeight,
                backgroundColor: finalButtonColor,
                color: colors.neutral.white,
                fontSize: isMobile ? '12px' : '13px',
                fontWeight: 600,
                fontFamily: theme.typography.bodyText.fontFamily,
                borderRadius: '8px',
                padding: isMobile ? '6px 12px' : '8px 16px',
                textTransform: 'none',
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: finalButtonColor,
                  filter: 'brightness(1.1)',
                  boxShadow: 'none',
                },
                '&.MuiButton-root': { boxShadow: 'none' },
              }}
            />
          )}

          {showCloseButton && (
            <IconButton
              onClick={onClose}
              sx={{
                width: isMobile ? '32px' : '36px',
                height: isMobile ? '32px' : '36px',
                padding: isMobile ? '6px' : '8px',
                borderRadius: '6px',
                color: finalIconColor,
                flexShrink: 0,
                marginLeft: 'auto',
                marginRight: '-4px',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }}
              aria-label="Close"
            >
              <CloseIcon sx={{ width: isMobile ? '18px' : '20px', height: isMobile ? '18px' : '20px' }} />
            </IconButton>
          )}
        </>
      )}
    </Box>
  );
};

export default StatusMessage;